import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { SiteSettings } from '../types';

interface BotoxEdegemProps {
  settings: SiteSettings;
}

const BotoxEdegem: React.FC<BotoxEdegemProps> = ({ settings }) => {
  return (
    <div className="bg-theme-bg pt-32 pb-24 transition-colors">
      <Helmet>
        <title>Botox Edegem | Natuurlijk Resultaat bij Dr. An De Cuyper</title>
        <meta name="description" content="Botox in Edegem voor fronsrimpels, voorhoofd en kraaienpootjes. Medische begeleiding door Dr. An De Cuyper met focus op natuurlijk resultaat." />
        <link rel="canonical" href="https://andecuyper.be/botox-edegem" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "BreadcrumbList",
                "@id": "https://andecuyper.be/botox-edegem#breadcrumb",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "Home",
                    "item": "https://andecuyper.be/"
                  },
                  {
                    "@type": "ListItem",
                    "position": 2,
                    "name": "Botox Edegem",
                    "item": "https://andecuyper.be/botox-edegem"
                  }
                ]
              },
              {
                "@type": "FAQPage",
                "@id": "https://andecuyper.be/botox-edegem#faq",
                "mainEntity": [
                  {
                    "@type": "Question",
                    "name": "Hoe snel zie ik resultaat na Botox?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "De eerste effecten van een Botox behandeling worden meestal merkbaar na 3 tot 5 dagen. Het uiteindelijke en optimale resultaat is na circa 14 dagen zichtbaar."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Hoe lang blijft Botox gemiddeld zichtbaar?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Gemiddeld blijft het effect van Botox 3 tot 5 maanden behouden. Dit varieert per persoon en is afhankelijk van uw metabolisme, levensstijl en spierkracht. Bij herhaalde behandelingen kan het resultaat langer aanhouden."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Wordt mijn gezicht stijf of onnatuurlijk?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Zeker niet. De behandelvisie 'less is more' staat centraal bij Dr. An De Cuyper. We streven altijd naar een frisse, ontspannen uitstraling waarbij uw natuurlijke mimiek en gelaatsexpressie gerespecteerd worden. De gevreesde 'frozen look' wordt vermeden door uiterst precieze en gedoseerde micro-injecties."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Is Botox geschikt voor fronsrimpels en voorhoofdsrimpels?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Ja, Botox (botulinetoxine) is de gouden standaard voor de behandeling van dynamische expressierimpels, zoals de verticale fronsrimpels tussen de wenkbrauwen, horizontale voorhoofdslijnen en kraaienpootjes rondom de ogen."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Kan ik eerst advies krijgen zonder meteen te behandelen?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Absoluut. Elke behandeling bij Clinic 2650 start met een uitgebreide, persoonlijke consultatie. Dr. An De Cuyper analyseert uw gelaatsanatomie, luistert naar uw wensen en bespreekt eerlijk de behandelopties. U bent nooit verplicht om direct over te gaan tot een behandeling."
                    }
                  }
                ]
              }
            ]
          })}
        </script>
      </Helmet>

      <section className="max-w-4xl mx-auto px-6">
        <span className="text-primary text-xs font-black uppercase tracking-[0.4em] mb-4 block">Edegem - Clinic 2650</span>
        <h1 className="font-display text-5xl md:text-7xl text-theme-text leading-tight mb-12 animate-fade-in">
          Botox in Edegem <br/>
          <span className="text-theme-muted italic">bij Dr. An De Cuyper</span>
        </h1>

        <div className="prose prose-lg prose-neutral dark:prose-invert max-w-none text-theme-muted font-light leading-relaxed space-y-8">
          <p className="text-xl text-theme-text font-medium leading-normal italic border-l-4 border-primary pl-8">
            Bent u op zoek naar een rimpelbehandeling in Edegem die zorgt voor een frissere, uitgeruste blik zonder dat uw gezichtsuitdrukking stijf of onnatuurlijk aanvoelt? Dr. An De Cuyper verwelkomt u graag bij Clinic 2650 voor een veilige, medisch onderbouwde en subtiele benadering.
          </p>

          <div className="my-8 flex flex-wrap gap-4">
            <a 
              href={settings.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-primary text-black px-8 py-4 rounded-xl font-black uppercase tracking-widest text-[10px] hover:bg-white transition-all inline-block"
            >
              Plan een persoonlijk consult
            </a>
            <Link 
              to="/tarieven"
              className="border border-theme-border text-theme-text px-8 py-4 rounded-xl font-black uppercase tracking-widest text-[10px] hover:bg-theme-surface transition-all inline-block"
            >
              Bekijk tarieven
            </Link>
          </div>

          <h2 className="text-3xl font-display text-theme-text pt-8">Wanneer is Botox in Edegem geschikt?</h2>
          <p>
            Botox (botulinetoxine) is bij uitstek geschikt voor het verzachten van dynamische rimpels – de lijnen die ontstaan door herhaaldelijke spierbewegingen en expressie van uw gezicht. In onze Edegemse praktijk Clinic 2650 behandelen we met name:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Fronsrimpels:</strong> De verticale rimpels tussen de wenkbrauwen die vaak een ongewenst strenge of bezorgde blik geven.</li>
            <li><strong>Voorhoofdsrimpels:</strong> De horizontale lijnen die ontstaan door het optrekken van het voorhoofd.</li>
            <li><strong>Kraaienpootjes:</strong> Lachrimpeltjes rondom de ogen die door een zachte ontspanning minder diep in de huid snijden.</li>
            <li><strong>Gummy Smile:</strong> Een overactieve spier die de bovenlip optrekt bij het lachen, waardoor er te veel tandvlees getoond wordt.</li>
            <li><strong>Masseter (Kaaklijn):</strong> Het ontspannen van de kauwmassa helpt bij het versmallen van de kaakcontour en biedt tevens verlichting bij tandenknarsen.</li>
            <li><strong>Overactieve aangezichtsmimiek:</strong> Algemene overactiviteit van de gelaatsspieren die de structuur van de huid langzaam beschadigt.</li>
          </ul>

          <h2 className="text-3xl font-display text-theme-text pt-8">Een natuurlijk resultaat staat centraal</h2>
          <p>
            Onze belangrijkste regel bij een rimpelbehandeling is absolute subtiliteit. We streven niet naar een onnatuurlijk gladgestreken gezicht waar alle emotie uit verdwenen is. Dr. An De Cuyper hanteert een conservatieve filosofie: er mag altijd bewuste beweging in uw gezicht overblijven. Een succesvolle behandeling is een behandeling waarbij anderen zien dat u er heerlijk uitgerust en gezond uitziet, zonder te weten hoe dat komt.
          </p>

          <h2 className="text-3xl font-display text-theme-text pt-8">Waarom kiezen voor Dr. An De Cuyper in Edegem?</h2>
          <p>
            Als gecertificeerd arts met een achtergrond in de algemene chirurgie beschikt Dr. An De Cuyper over een ongeëvenaarde kennis van de complexe anatomie van de gelaatshorizon. De juiste diepte van de injectie en de uiterst precieze positionering van de vloeistof bepalen het eindresultaat en de veiligheid.
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Arts-led expertise:</strong> Volledig medisch onderlegde behandelingen in overeenstemming met de strengste Belgische wetgeving.</li>
            <li><strong>Persoonlijk consult & continuïteit:</strong> U wordt altijd door dezelfde arts geconsulteerd, behandeld en gecontroleerd.</li>
            <li><strong>Eersteklas discretie:</strong> De praktijk Clinic 2650 in Edegem biedt een rustgevende, serene omgeving met makkelijke en discrete toegang.</li>
            <li><strong>FDA-goedgekeurde A-merken:</strong> We gebruiken uitsluitend hoogwaardige spierontspanners van bekende, streng gecontroleerde farmaceutische producenten.</li>
          </ul>

          <div className="my-8 p-8 bg-theme-surface rounded-2xl border border-theme-border text-center">
            <p className="text-lg text-theme-text font-medium mb-4">Wilt u uw opties bespreken met de arts?</p>
            <a 
              href={settings.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-primary text-black px-8 py-4 rounded-xl font-bold uppercase tracking-wider text-xs hover:bg-white transition-all inline-block"
            >
              Vraag advies aan Dr. An De Cuyper
            </a>
          </div>

          <h2 className="text-3xl font-display text-theme-text pt-8">Botox Edegem: praktisch verloop</h2>
          <p>
            Een Botox-behandeling is een snelle en comfortabele ingreep, vaak aangeduid als een 'lunchtime procedure'.
          </p>
          <ol className="list-decimal pl-6 space-y-3">
            <li><strong>Consultatie & Gelaatsanalyse:</strong> We nemen uitgebreid de tijd om uw mimiek te bestuderen, uw wensen in kaart te brengen en eventuele contra-indicaties (zoals zwangerschap) te controleren.</li>
            <li><strong>De Behandeling:</strong> De injectiezones worden gereinigd. Met een flinterdunne micronaald worden minimale hoeveelheden vloeistof ingebracht. De meeste cliënten ervaren dit als milde, korte speldenprikjes en hebben geen verdoving nodig.</li>
            <li><strong>Nazorg & Dagelijkse Routine:</strong> Na de behandeling raden we aan om gedurende 4 uur niet plat te liggen of te bukken. Tevens is het belangrijk om 24 uur niet intensief te sporten, de sauna te vermijden of de behandelde zone te masseren.</li>
            <li><strong>Evaluatie & Touch-up:</strong> Na ongeveer 2 weken evalueren we de resultaten. Waar nodig voeren we een kleine touch-up uit om maximale symmetrie te borgen.</li>
          </ol>

          <h2 className="text-3xl font-display text-theme-text pt-8">Veelgestelde Vragen over Botox (FAQ)</h2>
          <div className="space-y-6 not-prose mt-8">
            <div className="bg-theme-surface p-8 rounded-2xl border border-theme-border">
              <h3 className="font-bold text-lg text-theme-text mb-2">1. Hoe snel zie ik resultaat na Botox?</h3>
              <p className="text-sm opacity-80 text-theme-muted">De eerste effecten van een Botox behandeling worden meestal merkbaar na 3 tot 5 dagen. Het uiteindelijke en optimale resultaat is na circa 14 dagen zichtbaar.</p>
            </div>
            <div className="bg-theme-surface p-8 rounded-2xl border border-theme-border">
              <h3 className="font-bold text-lg text-theme-text mb-2">2. Hoe lang blijft Botox gemiddeld zichtbaar?</h3>
              <p className="text-sm opacity-80 text-theme-muted">Gemiddeld blijft het effect van Botox 3 tot 5 maanden behouden. Dit varieert per persoon en is afhankelijk van uw metabolisme, levensstijl en spierkracht. Bij herhaalde behandelingen kan het resultaat langer aanhouden.</p>
            </div>
            <div className="bg-theme-surface p-8 rounded-2xl border border-theme-border">
              <h3 className="font-bold text-lg text-theme-text mb-2">3. Wordt mijn gezicht stijf of onnatuurlijk?</h3>
              <p className="text-sm opacity-80 text-theme-muted">Zeker niet. De behandelvisie &#x27;less is more&#x27; staat centraal bij Dr. An De Cuyper. We streven altijd naar een frisse, ontspannen uitstraling waarbij uw natuurlijke mimiek en gelaatsexpressie gerespecteerd worden. De gevreesde &#x27;frozen look&#x27; wordt vermeden door uiterst precieze en gedoseerde micro-injecties.</p>
            </div>
            <div className="bg-theme-surface p-8 rounded-2xl border border-theme-border">
              <h3 className="font-bold text-lg text-theme-text mb-2">4. Is Botox geschikt voor fronsrimpels en voorhoofdsrimpels?</h3>
              <p className="text-sm opacity-80 text-theme-muted">Ja, Botox (botulinetoxine) is de gouden standaard voor de behandeling van dynamische expressierimpels, zoals de verticale fronsrimpels tussen de wenkbrauwen, horizontale voorhoofdslijnen en kraaienpootjes rondom de ogen.</p>
            </div>
            <div className="bg-theme-surface p-8 rounded-2xl border border-theme-border">
              <h3 className="font-bold text-lg text-theme-text mb-2">5. Kan ik eerst advies krijgen zonder meteen te behandelen?</h3>
              <p className="text-sm opacity-80 text-theme-muted">Absoluut. Elke behandeling bij Clinic 2650 start met een uitgebreide, persoonlijke consultatie. Dr. An De Cuyper analyseert uw gelaatsanatomie, luistert naar uw wensen en bespreekt eerlijk de behandelopties. U bent nooit verplicht om direct over te gaan tot een behandeling.</p>
            </div>
          </div>

          <div className="text-xs text-theme-muted mt-12 pt-6 border-t border-theme-border flex flex-col sm:flex-row justify-between gap-4">
            <span>Locatie: <Link to="/praktijk-edegem" className="underline hover:text-primary">Clinic 2650 Edegem</Link></span>
            <span>Andere behandelingen: <Link to="/fillers-antwerpen" className="underline hover:text-primary">Fillers Antwerpen</Link> | <Link to="/botox-antwerpen" className="underline hover:text-primary">Botox Antwerpen</Link> | <Link to="/tarieven" className="underline hover:text-primary">Tarieven</Link></span>
          </div>

          <div className="mt-16 bg-background-dark p-12 rounded-[3rem] text-center text-white border border-white/5">
            <h3 className="font-display text-4xl mb-6">Plan uw afspraak in Edegem</h3>
            <p className="mb-10 text-gray-400 max-w-xl mx-auto">
              Plan vandaag nog uw esthetische consultatie bij Dr. An De Cuyper in de serene sfeer van Clinic 2650.
            </p>
            <a 
              href={settings.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-primary text-black px-12 py-5 rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-white transition-all inline-block"
            >
              Maak een afspraak
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BotoxEdegem;
